import React, { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './Flex.module.css';

// ── CVA definition ─────────────────────────────────────────────────────────

const flex = cva(styles.base, {
  variants: {
    vertical: { true: styles.vertical },
    wrap: { true: styles.wrap },
    justify: {
      start: styles.justifyStart,
      center: styles.justifyCenter,
      end: styles.justifyEnd,
      'space-between': styles.justifySpaceBetween,
      'space-around': styles.justifySpaceAround,
      'space-evenly': styles.justifySpaceEvenly,
    },
    align: {
      start: styles.alignStart,
      center: styles.alignCenter,
      end: styles.alignEnd,
      stretch: styles.alignStretch,
      baseline: styles.alignBaseline,
    },
    gap: {
      xs: styles.gapXs,
      sm: styles.gapSm,
      md: styles.gapMd,
      lg: styles.gapLg,
      runtime: styles.gapRuntime,
    },
  },
});

// ── Types ──────────────────────────────────────────────────────────────────

type FlexGap = 'xs' | 'sm' | 'md' | 'lg' | number;

interface FlexProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  /** Stack children vertically */
  vertical?: boolean;
  /** Allow wrapping */
  wrap?: boolean;
  /** Main-axis alignment */
  justify?: 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly';
  /** Cross-axis alignment */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  /** Gap between children */
  gap?: FlexGap;
  /** Render as a different HTML element */
  component?: React.ElementType;
  children?: React.ReactNode;
}

/**
 * Flex — flexible box layout component.
 *
 * A thin wrapper around `display: flex` using design tokens for gap.
 * Supports ref forwarding.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | vertical | boolean | false | Column direction |
 * | wrap | boolean | false | Allow wrapping |
 * | justify | string | — | justify-content |
 * | align | string | — | align-items |
 * | gap | 'xs' \| 'sm' \| 'md' \| 'lg' \| number | — | Flex gap |
 * | component | string | 'div' | HTML element to render |
 *
 * @example
 * <Flex gap="md" align="center">
 *   <Avatar />
 *   <Text>Username</Text>
 * </Flex>
 */
export const Flex = forwardRef<HTMLElement, FlexProps>(function Flex(
  { vertical = false, wrap = false, justify, align, gap, component: Component = 'div', style, className, children, ...props },
  ref,
) {
  // Numeric gap is passed as a CSS custom property; named gap uses a class
  const isNumericGap = typeof gap === 'number';
  const gapVariant = isNumericGap ? 'runtime' : (gap as VariantProps<typeof flex>['gap']);

  const runtimeStyle = isNumericGap
    ? ({ '--flex-gap': `${gap}px`, ...style } as React.CSSProperties)
    : style;

  return (
    <Component
      ref={ref as React.Ref<HTMLElement>}
      className={flex({ vertical, wrap, justify, align, gap: gapVariant, className })}
      style={runtimeStyle}
      {...props}
    >
      {children}
    </Component>
  );
});
